import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { submitLogin ,submitLogout } from '@/helpers/api/user';
import type { Credentials } from '@/helpers/api/user';
import router from '@/router';
import { useRoute } from 'vue-router';
import _pick from 'lodash/pick';
import UserFlagsMapping from '@/data/mappings/UserFlags.map';
import AccessLevelsMapping from '@/data/mappings/AccessLevels.map';
import { fetchAvailableIntegrationInstances } from '@/helpers/api/player-subscription/fetch';
import { bugsnagService } from '@/config/bugsnag';


export const useUserStore = defineStore('user', () => {
  const userId = ref();
  const username = ref();
  const firstName = ref();
  const lastName = ref();
  const isLoggedIn = ref(false);
  const isAdmin = ref();
  const fullName = computed(() => `${firstName.value} ${lastName.value}`)
  const isLoginCheckedLocaly = ref(false);
  const error = ref();
  const passwordResetErrorMessage = ref();
  const isLoading = ref(false);
  // const roles = ref();
  const flags = ref();
  const areaAccessLevels = ref();
  const assetTypeAccessLevels = ref();
  const accountId = ref();
  const featureFlags = ref<Record<string, boolean>>({});
  const jwtToken = ref<string | null>(null);

  const route = useRoute();

  function checkLogin() {
    if(isLoginCheckedLocaly.value === true) {
      return
    }
    
    const userFromLocalStorage_string = localStorage.getItem('user') || '';
    if(!userFromLocalStorage_string) {    // user wasn't logged in before
      isLoggedIn.value = false;
    } else {
      isLoggedIn.value = true;
      copyUserFromLocalToState();
    }
    isLoginCheckedLocaly.value = true;
    redirectIfRouteRequiresAuth();
    return

    function copyUserFromLocalToState() {
      const userFromLocalStorage = JSON.parse(userFromLocalStorage_string);
      username.value = userFromLocalStorage.username;
      userId.value = userFromLocalStorage.userId;
      firstName.value = userFromLocalStorage.firstName;
      lastName.value = userFromLocalStorage.lastName;
      isAdmin.value = userFromLocalStorage.isAdmin;
      flags.value = userFromLocalStorage.flags;
      areaAccessLevels.value = userFromLocalStorage.areaAccessLevels;
      assetTypeAccessLevels.value = userFromLocalStorage.assetTypeAccessLevels;
      accountId.value = userFromLocalStorage.accountId;
      featureFlags.value = userFromLocalStorage.featureFlags ?? {};
      jwtToken.value = userFromLocalStorage.jwtToken ?? null;
      // roles.value = userFromLocalStorage.roles;
    }

    function redirectIfRouteRequiresAuth() {
      if (route.meta.requiresAuth && isLoggedIn.value === false) {
        router.push({ name: 'login' })
      }
    }
  }
  async function login({username: usernameInput, password}: Credentials) {
    try {
      isLoading.value = true;
      const responseData = await submitLogin({username: usernameInput, password})
      isLoading.value = false;

      bugsnagService.setUser(responseData.user.id, responseData.user.userName, responseData.user.firstName + ' ' + responseData.user.lastName)

      await checkAndUpdateFeatureFlags();

      username.value = responseData.user.userName;
      userId.value = responseData.user.id;
      firstName.value = responseData.user.firstName;
      lastName.value = responseData.user.lastName;
      isAdmin.value = responseData.user.isAdmin;
      flags.value = responseData.user.flags;
      areaAccessLevels.value = responseData.user.areaAccessLevels;
      assetTypeAccessLevels.value = responseData.user.assetTypeAccessLevels;
      accountId.value = responseData.user.roles?.[0]?.accountId;
      jwtToken.value = responseData.jwtToken ?? null;
      // roles.value = getRolesPrepared(responseData.user.roles);
      isLoggedIn.value = true;

      localStorage.setItem('user', JSON.stringify({
        username: username.value,
        userId: userId.value,
        firstName: firstName.value,
        lastName: lastName.value,
        isAdmin: isAdmin.value,
        flags: flags.value,
        areaAccessLevels: areaAccessLevels.value,
        assetTypeAccessLevels: assetTypeAccessLevels.value,
        accountId: accountId.value,
        // roles: roles.value
        featureFlags: featureFlags.value,
        jwtToken: jwtToken.value,
      }));

      error.value = null;
    } catch (err: any) {
      isLoading.value = false;
      error.value = err.message;
    }
  }

  /** Checks and updates feature flags based on the available integrations */
  async function checkAndUpdateFeatureFlags() {
    try {
      /** Integration Implementation Keys of the features to be flagged */
      const implementationKeys = ["liveStream"];
      const availableIntegrationsResponse = await fetchAvailableIntegrationInstances({
        filters: {
          implementationKeys,
        },
      });
      const availableIntegrations = availableIntegrationsResponse.results;
  
      const isLiveStreamIntegrationInstanceAvailable =
        availableIntegrations.some((integration: any) =>
          integration.integrationImplementation.implementationKey === "liveStream"
        );
      featureFlags.value["liveStream"] = isLiveStreamIntegrationInstanceAvailable;
    } catch (err: any) {
      if(err.message?.match(/\s403/)) {
        console.error(`Feature flags are not available for the user`);
        return;
      } else {
        console.error(`Error while checking and updating feature flags`, err);
        throw err;
      }
    }
  }

  async function logout() {
    try {
      await submitLogout()
    } catch(error) {
      console.error(`Error while submitting logout`, error)
    }
    if(this !== undefined) {
      this.$reset();
    }
    localStorage.removeItem('user');
    // redirect to login page if current path is authorized
    router.push({ name: 'login' })
  }

  interface Role {
    roleAreaAccesses: RoleAreaAccess[];
    roleAssetTypeAccesses: RoleAssetTypeAccess[];
  }

  interface RoleAreaAccess {
    access: string | number;
    area: {
      name: string;
      areaType: string | number;
    }
  }

  interface RoleAssetTypeAccess {
    access: string | number;
    assetType: string | number;
  }

  function getRolesPrepared(rolesReceived: Role[]) {
    const rolesPrepared: Role[] = [];
    rolesReceived.forEach((roleReceived: Role) => {
      const roleAreaAccessesPrepared: RoleAreaAccess[] = [];
      roleReceived.roleAreaAccesses.forEach(roleAreaAccess => {
        const roleAreaAccessPrepared = _pick(roleAreaAccess, ['access', 'area.name', 'area.areaType']);
        roleAreaAccessesPrepared.push(roleAreaAccessPrepared);
      })
      const roleAssetTypeAccessesPrepared: RoleAssetTypeAccess[] = [];
      roleReceived.roleAssetTypeAccesses.forEach(roleAssetTypeAccess => {
        const roleAssetTypeAccessPrepared = _pick(roleAssetTypeAccess, ['access', 'assetType']);
        roleAssetTypeAccessesPrepared.push(roleAssetTypeAccessPrepared);
      });
      
      const rolePrepared: Role = {
        roleAreaAccesses: roleAreaAccessesPrepared,
        roleAssetTypeAccesses: roleAssetTypeAccessesPrepared,
      };
      rolesPrepared.push(rolePrepared);
    });
    return rolesPrepared;
  }
  
  function isFlagSet(value: number, flag: number) {
    return ((value & flag) == flag);
  }

  function hasFlag(flagName: keyof typeof UserFlagsMapping) {
    if(isLoggedOutHandler()) return false;
    const userFlags = flags.value;
    return isFlagSet(userFlags, UserFlagsMapping[flagName]);
  }

  function hasAccessToFeature(featureFlagName: string) {
    
    if(isLoggedOutHandler()) return false;
    const userEnabledFeatureFlags = Object.keys(featureFlags.value).filter(
      featureFlagNameI => featureFlags.value[featureFlagNameI]
    );
    return userEnabledFeatureFlags.some(featureFlagNameI => featureFlagNameI === featureFlagName);
  }

  function isSuperUser() {
    return hasFlag("SuperUser");
  }
  function isAccountAdmin() {
    return hasFlag("AccountAdmin");
  }
  function isAuditor() {
    return hasFlag("Auditor") || isSuperUser();
  }
  function isAnalytics() {
    return hasFlag("Analytics") || isSuperUser() || isAccountAdmin();
  }

  function hasReadAccessToArea(areaName: string) {
    if(isLoggedOutHandler()) return false;
    const userAreaAccessLevelFlag = areaAccessLevels.value[areaName];
    return isFlagSet(userAreaAccessLevelFlag, AccessLevelsMapping["Read"]);
  }

  function hasWriteAccessToArea(areaName: string) {
    if(isLoggedOutHandler()) return false;
    const userAreaAccessLevelFlag = areaAccessLevels.value[areaName];
    return isFlagSet(userAreaAccessLevelFlag, AccessLevelsMapping["Write"]);
  }

  function hasCreateAccessToArea(areaName: string) {
    if(isLoggedOutHandler()) return false;
    const userAreaAccessLevelFlag = areaAccessLevels.value[areaName];
    return isFlagSet(userAreaAccessLevelFlag, AccessLevelsMapping["Create"]);
  }

  function hasDeleteAccessToArea(areaName: string) {
    if(isLoggedOutHandler()) return false;
    const userAreaAccessLevelFlag = areaAccessLevels.value[areaName];
    return isFlagSet(userAreaAccessLevelFlag, AccessLevelsMapping["Delete"]);
  }

  type AssetType = "Image" | "Video" | "Slide";

  function hasReadAccessToAssetType(assetType: AssetType) {
    if(isLoggedOutHandler()) return false;
    if (assetType === undefined) return true;
    const assetTypePrepared = assetType[0].toUpperCase() + assetType.substring(1);
    const userAssetTypeAccessLevel = assetTypeAccessLevels.value[assetTypePrepared];
    return isFlagSet(userAssetTypeAccessLevel, AccessLevelsMapping["Read"]);
  }

  function hasWriteAccessToAssetType(assetType: AssetType) {
    if(isLoggedOutHandler()) return false;
    if (assetType === undefined) return true;
    const assetTypePrepared = assetType[0].toUpperCase() + assetType.substring(1);
    const userAssetTypeAccessLevel = assetTypeAccessLevels.value[assetTypePrepared];
    return isFlagSet(userAssetTypeAccessLevel, AccessLevelsMapping["Write"]);
  }

  function hasCreateAccessToAssetType(assetType: AssetType) {
    if(isLoggedOutHandler()) return false;
    if (assetType === undefined) return true;
    const assetTypePrepared = assetType[0].toUpperCase() + assetType.substring(1);
    const userAssetTypeAccessLevel = assetTypeAccessLevels.value[assetTypePrepared];
    return isFlagSet(userAssetTypeAccessLevel, AccessLevelsMapping["Create"]);
  }

  function hasDeleteAccessToAssetType(assetType: AssetType) {
    if(isLoggedOutHandler()) return false;
    if (assetType === undefined) return true;
    const assetTypePrepared = assetType[0].toUpperCase() + assetType.substring(1);
    const userAssetTypeAccessLevel = assetTypeAccessLevels.value[assetTypePrepared];
    return isFlagSet(userAssetTypeAccessLevel, AccessLevelsMapping["Delete"]);
  }

  function hasReadAccessToItem(item: any) {
    return isFlagSet(item.accessLevel, AccessLevelsMapping["Read"]);
  }

  function hasWriteAccessToItem(item: any) {
    return isFlagSet(item.accessLevel, AccessLevelsMapping["Write"]);
  }

  function hasCreateAccessToItem(item: any) {
    return isFlagSet(item.accessLevel, AccessLevelsMapping["Create"]);
  }

  function hasDeleteAccessToItem(item: any) {
    return isFlagSet(item.accessLevel, AccessLevelsMapping["Delete"]);
  }

  function isLoggedOutHandler() {
    if(areaAccessLevels.value === undefined || assetTypeAccessLevels.value === undefined) {
      // important for "Dashboard" page if old users that don't have assetTypeAccessLevels stored in localStorage
      logout();
      return true;
    }
  }

  return { 
    userId,
    username,
    firstName,
    lastName,
    isLoggedIn,
    isAdmin,
    // roles,
    areaAccessLevels,
    assetTypeAccessLevels,
    accountId,
    fullName,
    isLoginCheckedLocaly,
    jwtToken,

    login,
    logout,
    checkLogin,
    
    hasFlag,
    hasAccessToFeature,
    isSuperUser,
    isAccountAdmin,
    isAuditor,
    isAnalytics,

    hasReadAccessToArea,
    hasWriteAccessToArea,
    hasCreateAccessToArea,
    hasDeleteAccessToArea,

    hasReadAccessToAssetType,
    hasWriteAccessToAssetType,
    hasCreateAccessToAssetType,
    hasDeleteAccessToAssetType,

    hasReadAccessToItem,
    hasWriteAccessToItem,
    hasCreateAccessToItem,
    hasDeleteAccessToItem,

    error,
    isLoading,
    passwordResetErrorMessage
  }
});

export default useUserStore;